import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FaExternalLinkAlt, FaHubspot } from "react-icons/fa";
import { useClientOverview } from "../ClientOverviewContext";
import { formatDate, formatNumber, hubspotCompanyUrl, hubspotDealUrl } from "../selectors";
import { ActivityStrip } from "./ActivityStrip";

const PEOPLE_PREVIEW = 10;

/** "document_upload" → "Document uploads" style labels for action types. */
const ACTION_LABELS = {
  loan_opened: "Loans opened",
  document_upload: "Documents uploaded",
  checklist_run: "Checklist runs",
  processing_checklist_run: "Checklist runs",
  ask_addy_question: "Ask Addy questions",
  income_calculation: "Income calculations",
};

function actionLabel(type) {
  if (ACTION_LABELS[type]) {
    return ACTION_LABELS[type];
  }
  const words = String(type).replace(/[_-]+/g, " ").trim();

  return words.charAt(0).toUpperCase() + words.slice(1);
}

function HubSpotLinks({ companyId, dealId }) {
  return (
    <>
      {companyId ? (
        <a
          className="pl-link"
          href={hubspotCompanyUrl(companyId)}
          target="_blank"
          rel="noopener noreferrer"
        >
          <FaHubspot aria-hidden="true" /> Company in HubSpot
        </a>
      ) : null}
      {dealId ? (
        <a className="pl-link" href={hubspotDealUrl(dealId)} target="_blank" rel="noopener noreferrer">
          <FaHubspot aria-hidden="true" /> Deal in HubSpot
        </a>
      ) : null}
    </>
  );
}

/**
 * Everything happening at one client: who is in, who has not accepted, how
 * many loans, what each person did each day, and what they asked Ask Addy.
 *
 * @param {object} props
 * @param {string} props.organizationId
 * @param {boolean} [props.includeHubSpot] resolve HubSpot links server-side
 *   (for clients reached without a HubSpot deal)
 * @param {string|null} [props.companyId] HubSpot company, when already known
 * @param {string|null} [props.dealId] HubSpot deal, when already known
 */
export function ClientOverview({ organizationId, includeHubSpot = false, companyId = null, dealId = null }) {
  const { loading, error, data, retry } = useClientOverview(organizationId, { includeHubSpot });
  const [showAllPeople, setShowAllPeople] = useState(false);

  if (loading && !data) {
    return <div className="pl-overview pl-empty">Loading usage from Addy…</div>;
  }
  if (error) {
    return (
      <div className="pl-overview pl-inline-error" role="alert">
        Could not load this client's usage.
        <button type="button" className="pl-btn pl-btn--small" onClick={retry}>
          Try again
        </button>
      </div>
    );
  }
  if (!data) {
    return null;
  }

  const people = showAllPeople ? data.people : data.people.slice(0, PEOPLE_PREVIEW);
  const max = Math.max(0, ...data.people.flatMap((person) => person.daily));
  const hubspot = {
    companyId: companyId || data.hubspot?.companyId || null,
    dealId: dealId || data.hubspot?.dealId || null,
  };

  return (
    <div className="pl-overview">
      <div className="pl-overview-head">
        <h4>Usage in Addy · last {data.days.length} days</h4>
        <div className="pl-overview-links">
          <HubSpotLinks companyId={hubspot.companyId} dealId={hubspot.dealId} />
          <Link className="pl-link" to={`/org-usage-analytics/${organizationId}`}>
            Full usage page <FaExternalLinkAlt aria-hidden="true" />
          </Link>
        </div>
      </div>

      <div className="pl-chip-row">
        <span className="pl-chip">
          {formatNumber(data.team.invited)} people · {formatNumber(data.team.accepted)} accepted
          {data.team.pending ? ` · ${formatNumber(data.team.pending)} not accepted yet` : ""}
        </span>
        <span className="pl-chip">
          {formatNumber(data.team.active)} active in the last {data.days.length} days
        </span>
        <span className="pl-chip">{formatNumber(data.loansCount)} loans in Addy</span>
        <span className="pl-chip">{formatNumber(data.totalEvents)} actions</span>
      </div>

      {data.activityByType?.length ? (
        <p className="pl-note pl-overview-actions">
          Mostly:{" "}
          {data.activityByType
            .map((entry) => `${actionLabel(entry.type)} ${formatNumber(entry.count)}`)
            .join(" · ")}
        </p>
      ) : null}

      <div className="pl-overview-grid">
        <div>
          <h5 className="pl-overview-subtitle">People</h5>
          {data.people.length ? (
            <div className="pl-table-wrap">
              <table className="pl-table pl-people-table">
                <thead>
                  <tr>
                    <th className="pl-left">Person</th>
                    <th className="pl-left">Each day</th>
                    <th>Actions</th>
                    <th>Loans</th>
                    <th>Last active</th>
                  </tr>
                </thead>
                <tbody>
                  {people.map((person) => (
                    <tr key={person.email}>
                      <td className="pl-left">
                        <span className="pl-person-name">{person.name || person.email}</span>
                        <span className="pl-deal-sub">
                          {person.status === "pending" ? (
                            <span className="pl-badge pl-badge--warn">Not accepted</span>
                          ) : (
                            person.role || person.email
                          )}
                        </span>
                      </td>
                      <td className="pl-left">
                        <ActivityStrip
                          days={data.days}
                          values={person.daily}
                          max={max}
                          label={person.name || person.email}
                        />
                      </td>
                      <td>{formatNumber(person.totalEvents)}</td>
                      <td>{formatNumber(person.loansTouched)}</td>
                      <td>{person.lastActiveAt ? formatDate(person.lastActiveAt) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="pl-empty">Nobody has been invited yet.</div>
          )}
          {data.people.length > PEOPLE_PREVIEW ? (
            <button
              type="button"
              className="pl-btn pl-btn--small"
              onClick={() => setShowAllPeople((current) => !current)}
            >
              {showAllPeople ? "Show fewer" : `Show all ${formatNumber(data.people.length)} people`}
            </button>
          ) : null}
        </div>

        <div>
          <h5 className="pl-overview-subtitle">Latest Ask Addy questions</h5>
          {data.questions.length ? (
            <ul className="pl-questions">
              {data.questions.map((entry, index) => (
                <li key={`${entry.askedAt}-${index}`}>
                  <q>{entry.question}</q>
                  <span className="pl-deal-sub">
                    {[entry.askedBy, entry.askedAt ? formatDate(entry.askedAt) : null]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="pl-empty">No Ask Addy questions yet.</div>
          )}
        </div>
      </div>
    </div>
  );
}
